import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, Sparkles, Crown, Building2, Users, ChevronDown, ChevronUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PLANS, ADDONS, FAQ_PLANS } from '../mockPlans';
import SEO from '../components/SEO';
import { formatCOP } from '../mock';
import { Button } from '../components/ui/button';

const AUDIENCE_TABS = [
  { id: 'all', label: 'Todos los planes' },
  { id: 'propietario', label: 'Para propietarios' },
  { id: 'agente', label: 'Para agentes' },
  { id: 'inmobiliaria', label: 'Para inmobiliarias' },
  { id: 'constructora', label: 'Para constructoras' }
];

export default function Pricing() {
  const [billing, setBilling] = useState('mes');
  const [audience, setAudience] = useState('all');
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const filtered = PLANS.filter(p => audience === 'all' || p.audience === audience);

  const calcPrice = (p) => {
    if (p.price === 0) return 0;
    return billing === 'anio' ? Math.round(p.price * 10) : p.price;
  };

  const planIcons = {
    gratis: Sparkles,
    starter: Users,
    pro: Crown,
    inmobiliaria: Building2,
    constructora: Building2
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Planes y precios"
        description="Planes de habita.co para inmobiliarias, agentes independientes y propietarios en Colombia. Publica propiedades, capta clientes y usa el Habita Advisor sin fricción."
      />
      <Header />

      {/* Hero */}
      <section className="bg-habita-cream py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-habita-mint text-habita rounded-full text-xs font-semibold tracking-wide">
            <Sparkles className="w-3.5 h-3.5" /> BETA GRATUITA · ACCESO ANTICIPADO
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-habita-dark mt-4 leading-[1.05]">
            Todo <span className="italic text-amber-600">gratis</span> durante la beta.
          </h1>
          <p className="text-gray-600 mt-5 text-lg max-w-2xl mx-auto">
            Estamos validando habita.co con nuestros primeros usuarios. Disfruta acceso anticipado a todas las funcionalidades premium sin costo mientras dure la beta.
          </p>

          {/* BETA DISCLAIMER */}
          <div className="mt-7 max-w-2xl mx-auto bg-white border border-amber-200 rounded-2xl p-5 flex items-start gap-3 text-left">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-amber-600" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-habita-dark">Durante la beta, las funcionalidades premium están disponibles sin costo.</p>
              <p className="text-gray-600 mt-1">Los precios mostrados son referenciales para cuando lancemos oficialmente. Te avisaremos con 30 días de anticipación antes de activar cobros.</p>
            </div>
          </div>

          {/* Audience tabs */}
          <div className="mt-8 inline-flex items-center gap-1 bg-white rounded-full p-1.5 border border-gray-200 flex-wrap justify-center">
            {AUDIENCE_TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setAudience(t.id)}
                data-testid={`audience-tab-${t.id}`}
                className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${audience === t.id ? 'bg-habita text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Plans grid */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 -mt-10">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6">
          {filtered.map(plan => {
            const Icon = planIcons[plan.id] || Sparkles;
            const featured = plan.id === 'pro';
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl p-7 flex flex-col ${featured ? 'bg-habita-dark text-white shadow-2xl scale-[1.02] border-2 border-amber-500' : 'bg-white border border-gray-200 hover-lift'}`}
              >
                {/* BETA GRATUITA badge — always visible on every card */}
                <span className={`absolute -top-3 right-5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${featured ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'}`}>
                  Beta gratuita
                </span>
                {plan.badge && (
                  <span className={`absolute -top-3 left-5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${featured ? 'bg-white text-habita-dark' : 'bg-habita-mint text-habita'}`}>
                    {plan.badge}
                  </span>
                )}
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mt-2 ${featured ? 'bg-amber-500/20 text-amber-300' : 'bg-habita-mint text-habita'}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className={`font-serif text-3xl font-bold mt-4 ${featured ? 'text-white' : 'text-habita-dark'}`}>{plan.name}</h3>
                <p className={`text-sm mt-2 ${featured ? 'text-white/70' : 'text-gray-600'}`}>{plan.description}</p>

                <div className="mt-6 mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className={`font-serif text-5xl font-bold ${featured ? 'text-white' : 'text-habita-dark'}`}>$0</span>
                    <span className={`text-sm ${featured ? 'text-white/70' : 'text-gray-500'}`}>durante la Beta</span>
                  </div>
                  {plan.price > 0 && (
                    <p className={`text-xs mt-2 ${featured ? 'text-white/50' : 'text-gray-400'}`}>
                      <span className="line-through">{formatCOP(plan.price)}/mes</span>
                      <span className="ml-2 italic">precio referencial post-beta</span>
                    </p>
                  )}
                </div>

                <Button
                  onClick={() => plan.id === 'gratis' ? navigate('/publicar') : navigate(`/checkout/${plan.id}?billing=mes`)}
                  data-testid={`plan-cta-${plan.id}`}
                  className={`w-full h-12 rounded-full font-semibold ${featured ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'btn-habita'}`}
                >
                  {plan.id === 'gratis' ? plan.cta : 'Activar acceso beta'}
                </Button>

                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${featured ? 'text-amber-400' : 'text-habita'}`} />
                      <span className={featured ? 'text-white/90' : 'text-gray-700'}>{f}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map(f => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm ${featured ? 'text-white/40' : 'text-gray-400'} line-through`}>
                      <X className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Add-ons — DISABLED DURING BETA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Próximamente</span>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark mt-2">Impulsa publicaciones específicas</h2>
          <p className="text-gray-600 mt-2">Compras únicas para destacar tu propiedad. Estarán disponibles cuando termine la beta.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {ADDONS.map(a => (
            <div key={a.id} className="relative bg-habita-cream rounded-2xl p-6 opacity-75">
              <span className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-gray-200 text-gray-600">Próximamente</span>
              <p className="font-serif text-2xl font-bold text-habita-dark pr-20">{a.name}</p>
              <p className="text-sm text-gray-600 mt-2">{a.description}</p>
              <div className="mt-5 flex items-baseline justify-between">
                <p className="text-sm text-gray-400 italic">No disponible en beta</p>
                <span className="text-sm font-semibold text-gray-400">—</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">Comparativa de planes</h2>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-habita-cream">
                <th className="text-left p-5 text-sm font-semibold text-habita-dark">Característica</th>
                {PLANS.map(p => (
                  <th key={p.id} className="p-5 text-sm font-semibold text-habita-dark text-center">{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                ['Publicaciones activas', p => p.limits.properties],
                ['Fotos por inmueble', p => p.limits.photos],
                ['Días de publicación', p => `${p.limits.duration} días`],
                ['Destacados incluidos / mes', p => p.limits.featured],
                ['Estadísticas', p => p.id === 'gratis' ? '—' : p.id === 'starter' ? 'Básicas' : 'Avanzadas'],
                ['Verificación profesional', p => ['pro', 'inmobiliaria'].includes(p.id) ? '✓' : '—'],
                ['Multi-agente', p => p.id === 'inmobiliaria' ? 'Hasta 10' : '—'],
                ['Soporte', p => p.id === 'gratis' ? 'Email' : p.id === 'inmobiliaria' ? 'Gerente dedicado' : 'Prioritario']
              ].map(([label, fn], i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="p-4 font-medium text-gray-700">{label}</td>
                  {PLANS.map(p => (
                    <td key={p.id} className="p-4 text-center text-gray-700">{fn(p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Preguntas frecuentes</span>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark mt-2">¿Tienes dudas?</h2>
        </div>
        <div className="space-y-3">
          {FAQ_PLANS.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50">
                <span className="font-semibold text-habita-dark">{f.q}</span>
                {openFaq === i ? <ChevronUp className="w-5 h-5 text-habita" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-20">
        <div className="bg-habita-dark rounded-3xl p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl" />
          <h2 className="relative font-serif text-3xl lg:text-5xl font-bold text-white">¿Listo para empezar?</h2>
          <p className="relative text-white/80 mt-3 max-w-xl mx-auto">Más de 25.000 propiedades publicadas en habita.co. Únete a nuestra comunidad de propietarios, agentes e inmobiliarias.</p>
          <div className="relative mt-7 flex gap-3 justify-center flex-wrap">
            <Link to="/publicar" className="px-8 h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold flex items-center">Publicar gratis</Link>
            <Link to="/checkout/pro" className="px-8 h-12 rounded-full border border-white/30 text-white hover:bg-white/10 font-semibold flex items-center">Probar Pro</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
